import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Button, Select } from 'ui/baseComponents'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import CodeIcon from '@mui/icons-material/Code'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease'
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import LinkIcon from '@mui/icons-material/Link'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import SmartButtonIcon from '@mui/icons-material/SmartButton'
import HeightIcon from '@mui/icons-material/Height'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import { Node, mergeAttributes } from '@tiptap/core'
import TiptapListItem from '@tiptap/extension-list-item'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { GET_TEMPLATE, useTemplateList } from '../graphql/templateQueries'
import {
  CREATE_TEMPLATE,
  UPDATE_TEMPLATE,
  DELETE_TEMPLATE,
} from '../graphql/templateMutations'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  TEMPLATE_CHANNELS,
  TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLE_LABELS,
} from '../consts'
import TemplatePreviewBox from '../components/TemplatePreviewBox'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const ButtonLink = Node.create({
  name: 'buttonLink',
  group: 'inline',
  inline: true,
  content: 'text*',

  addAttributes() {
    return {
      href: { default: null },
      target: {
        default: '_blank',
        renderHTML: (attrs) => ({ target: attrs.target }),
      },
      rel: {
        default: 'noopener noreferrer',
        renderHTML: (attrs) => ({ rel: attrs.rel }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'a[data-button-link]', priority: 60 }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-button-link': '',
        style:
          'display: inline-block; padding: 10px 24px; ' +
          'background-color: #1976d2; color: #FFFFFF; ' +
          'text-decoration: none; border-radius: 4px; ' +
          'font-weight: bold; text-align: center;',
      }),
      ['span', { style: 'color: #FFFFFF;' }, 0],
    ]
  },
})

const VerticalSpacer = Node.create({
  name: 'verticalSpacer',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      height: {
        default: 20,
        parseHTML: (el) => {
          const match = (el.getAttribute('style') || '').match(
            /height:\s*(\d+)px/,
          )
          return match ? parseInt(match[1], 10) : 20
        },
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-spacer]' }]
  },

  renderHTML({ HTMLAttributes }) {
    const height = HTMLAttributes.height || 20
    return [
      'div',
      {
        'data-spacer': '',
        style: `height: ${height}px;`,
      },
    ]
  },
})

const buildJotformHref = (baseUrl, prefills) => {
  if (!baseUrl) return ''
  const params = prefills
    .filter((p) => p.key && p.variable)
    .map((p) => `${encodeURIComponent(p.key)}={{${p.variable}}}`)
    .join('&')
  return params ? `${baseUrl}?${params}` : baseUrl
}

const parseJotformHref = (href) => {
  if (!href) return { baseUrl: '', prefills: [] }
  const qIndex = href.indexOf('?')
  if (qIndex === -1) return { baseUrl: href, prefills: [] }
  const baseUrl = href.substring(0, qIndex)
  const query = href.substring(qIndex + 1)
  const prefills = query
    .split('&')
    .map((param) => {
      const eqIndex = param.indexOf('=')
      if (eqIndex === -1) return null
      const key = decodeURIComponent(param.substring(0, eqIndex))
      const rawValue = param.substring(eqIndex + 1)
      const varMatch = rawValue.match(/^\{\{(\w+)\}\}$/)
      return varMatch ? { key, variable: varMatch[1] } : null
    })
    .filter(Boolean)
  return { baseUrl, prefills }
}

const JotformLink = Node.create({
  name: 'jotformLink',
  group: 'inline',
  inline: true,
  content: 'text*',

  addAttributes() {
    return {
      href: { default: null },
      'data-jotform-link': {
        default: '',
        renderHTML: () => ({ 'data-jotform-link': '' }),
      },
      target: {
        default: '_blank',
        renderHTML: (attrs) => ({ target: attrs.target }),
      },
      rel: {
        default: 'noopener noreferrer',
        renderHTML: (attrs) => ({ rel: attrs.rel }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'a[data-jotform-link]', priority: 70 }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-button-link': '',
        style:
          'display: inline-block; padding: 10px 24px; ' +
          'background-color: #1976d2; color: #FFFFFF; ' +
          'text-decoration: none; border-radius: 4px; ' +
          'font-weight: bold; text-align: center;',
      }),
      ['span', { style: 'color: #FFFFFF;' }, 0],
    ]
  },
})

const JotformLinkDialog = ({ open, onClose, onSave, initial }) => {
  const [baseUrl, setBaseUrl] = useState('')
  const [prefills, setPrefills] = useState([])
  const [buttonText, setButtonText] = useState('')

  useEffect(() => {
    if (open) {
      if (initial) {
        setBaseUrl(initial.baseUrl || '')
        setPrefills(
          initial.prefills?.length
            ? initial.prefills
            : [{ key: '', variable: '' }],
        )
        setButtonText(initial.buttonText || '')
      } else {
        setBaseUrl('')
        setPrefills([{ key: '', variable: '' }])
        setButtonText('')
      }
    }
  }, [open, initial])

  const handleVariableChange = (index, variable) => {
    setPrefills((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, variable, key: p.key || variable } : p,
      ),
    )
  }

  const handleKeyChange = (index, key) => {
    setPrefills((prev) => prev.map((p, i) => (i === index ? { ...p, key } : p)))
  }

  const addPrefill = () => {
    setPrefills((prev) => [...prev, { key: '', variable: '' }])
  }

  const removePrefill = (index) => {
    setPrefills((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    const href = buildJotformHref(baseUrl, prefills)
    onSave({
      baseUrl,
      prefills: prefills.filter((p) => p.key && p.variable),
      href,
      buttonText: buttonText || 'TAKE THE SCREENER',
    })
  }

  const variableOptions = Object.keys(TEMPLATE_VARIABLES).map((v) => ({
    value: v,
    name: TEMPLATE_VARIABLE_LABELS[v] || v,
  }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', mt: 8 } }}
    >
      <DialogTitle>
        {initial ? 'Edit Jotform Link' : 'Insert Jotform Link'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            autoFocus
            label="Jotform Base URL"
            fullWidth
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://runelabs.jotform.com/team/ops/..."
          />
          {!initial && (
            <TextField
              label="Button Text"
              fullWidth
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="TAKE THE SCREENER"
            />
          )}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Prefilled Fields
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map query parameters to template variables
          </Typography>
          {prefills.map((prefill, index) => (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="Field name"
                value={prefill.key}
                onChange={(e) => handleKeyChange(index, e.target.value)}
              />
              <Autocomplete
                size="small"
                options={variableOptions}
                getOptionLabel={(opt) =>
                  typeof opt === 'string' ? opt : opt.name
                }
                value={
                  variableOptions.find((o) => o.value === prefill.variable) ||
                  null
                }
                onChange={(_, newVal) =>
                  handleVariableChange(index, newVal ? newVal.value : '')
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {'{{'}
                        {option.value}
                        {'}}'}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Variable" />
                )}
              />
              <IconButton
                size="small"
                onClick={() => removePrefill(index)}
                disabled={prefills.length <= 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            type="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={addPrefill}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Field
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSave} disabled={!baseUrl}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
  <IconButton
    size="small"
    onClick={onClick}
    disabled={disabled}
    title={title}
    sx={{
      borderRadius: 1,
      p: 0.5,
      color: active ? 'primary.main' : 'text.secondary',
      bgcolor: active ? 'action.selected' : 'transparent',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    {children}
  </IconButton>
)

const LinkBubble = ({ editor }) => {
  const [url, setUrl] = useState('')

  useEffect(() => {
    if (!editor) return () => {}
    const updateUrl = () => {
      if (editor.isActive('link')) {
        setUrl(editor.getAttributes('link').href || '')
      }
    }
    editor.on('selectionUpdate', updateUrl)
    return () => editor.off('selectionUpdate', updateUrl)
  }, [editor])

  if (!editor) return null

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor: ed }) => ed.isActive('link')}
      tippyOptions={{ maxWidth: 500 }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          p: 1,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 3,
          alignItems: 'center',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
        }}
      >
        <TextField
          size="small"
          placeholder="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run()
            }
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          type="primary"
          size="small"
          onClick={() => {
            editor
              .chain()
              .focus()
              .extendMarkRange('link')
              .setLink({ href: url })
              .run()
          }}
        >
          Save
        </Button>
        <Button
          type="secondary"
          size="small"
          onClick={() => {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            setUrl('')
          }}
          sx={{ flexShrink: 0 }}
        >
          Remove
        </Button>
      </Box>
    </BubbleMenu>
  )
}

const ButtonLinkBubble = ({ editor }) => {
  const [url, setUrl] = useState('')

  useEffect(() => {
    if (!editor) return () => {}
    const updateUrl = () => {
      const { $from } = editor.state.selection
      if ($from.parent.type.name === 'buttonLink') {
        setUrl($from.parent.attrs.href || '')
      }
    }
    editor.on('selectionUpdate', updateUrl)
    return () => editor.off('selectionUpdate', updateUrl)
  }, [editor])

  if (!editor) return null

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor: ed }) => {
        const { $from } = ed.state.selection
        return $from.parent.type.name === 'buttonLink'
      }}
      tippyOptions={{ maxWidth: 500 }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          p: 1,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 3,
          alignItems: 'center',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
        }}
      >
        <TextField
          size="small"
          placeholder="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const { $from } = editor.state.selection
              const nodeStart = $from.before()
              editor
                .chain()
                .focus()
                .command(({ tr }) => {
                  tr.setNodeMarkup(nodeStart, null, {
                    ...$from.parent.attrs,
                    href: url,
                  })
                  return true
                })
                .run()
            }
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          type="primary"
          size="small"
          onClick={() => {
            const { $from } = editor.state.selection
            const nodeStart = $from.before()
            editor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.setNodeMarkup(nodeStart, null, {
                  ...$from.parent.attrs,
                  href: url,
                })
                return true
              })
              .run()
          }}
        >
          Save
        </Button>
        <Button
          type="secondary"
          size="small"
          onClick={() => {
            const { $from } = editor.state.selection
            const nodeStart = $from.before()
            const nodeEnd = $from.after()
            editor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.delete(nodeStart, nodeEnd)
                return true
              })
              .run()
          }}
          sx={{ flexShrink: 0 }}
        >
          Remove
        </Button>
      </Box>
    </BubbleMenu>
  )
}

const JotformLinkBubble = ({ editor }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [initial, setInitial] = useState(null)

  if (!editor) return null

  const openEdit = () => {
    const { $from } = editor.state.selection
    const node = $from.parent
    if (node.type.name !== 'jotformLink') return
    const { baseUrl, prefills } = parseJotformHref(node.attrs.href)
    setInitial({
      baseUrl,
      prefills,
      buttonText: node.textContent,
    })
    setDialogOpen(true)
  }

  const handleSave = ({ href }) => {
    const { $from } = editor.state.selection
    const nodeStart = $from.before()
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setNodeMarkup(nodeStart, null, {
          ...$from.parent.attrs,
          href,
        })
        return true
      })
      .run()
    setDialogOpen(false)
  }

  return (
    <>
      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: ed }) => {
          const { $from } = ed.state.selection
          return $from.parent.type.name === 'jotformLink'
        }}
        tippyOptions={{ maxWidth: 500 }}
      >
        <Box
          sx={{
            display: dialogOpen ? 'none' : 'flex',
            gap: 1,
            p: 1,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            Jotform Link
          </Typography>
          <Button
            type="secondary"
            size="small"
            startIcon={<EditIcon />}
            onClick={openEdit}
          >
            Edit
          </Button>
          <Button
            type="secondary"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => {
              const { $from } = editor.state.selection
              const nodeStart = $from.before()
              const nodeEnd = $from.after()
              editor
                .chain()
                .focus()
                .command(({ tr }) => {
                  tr.delete(nodeStart, nodeEnd)
                  return true
                })
                .run()
            }}
          >
            Remove
          </Button>
        </Box>
      </BubbleMenu>
      <JotformLinkDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initial={initial}
      />
    </>
  )
}

const EditorToolbar = ({ editor }) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [buttonLinkDialogOpen, setButtonLinkDialogOpen] = useState(false)
  const [jotformDialogOpen, setJotformDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  if (!editor) return null

  const handleLinkSubmit = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run()
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    }
    setLinkUrl('')
    setLinkDialogOpen(false)
  }

  const handleButtonLinkSubmit = () => {
    if (!linkUrl) {
      setLinkUrl('')
      setButtonLinkDialogOpen(false)
      return
    }
    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to)
    if (selectedText) {
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertContent({
          type: 'buttonLink',
          attrs: { href: linkUrl },
          content: [{ type: 'text', text: selectedText }],
        })
        .run()
    }
    setLinkUrl('')
    setButtonLinkDialogOpen(false)
  }

  const openLinkDialog = () => {
    const existing = editor.getAttributes('link').href || ''
    setLinkUrl(existing)
    setLinkDialogOpen(true)
  }

  const openButtonLinkDialog = () => {
    setLinkUrl('')
    setButtonLinkDialogOpen(true)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.25,
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
        >
          <StrikethroughSIcon fontSize="small" />
        </ToolbarButton>

        <Box sx={{ borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().liftListItem('listItem').run()}
          disabled={!editor.can().liftListItem('listItem')}
          title="Decrease indent"
        >
          <FormatIndentDecreaseIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
          disabled={!editor.can().sinkListItem('listItem')}
          title="Increase indent"
        >
          <FormatIndentIncreaseIcon fontSize="small" />
        </ToolbarButton>

        <Box sx={{ borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align left"
        >
          <FormatAlignLeftIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align center"
        >
          <FormatAlignCenterIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align right"
        >
          <FormatAlignRightIcon fontSize="small" />
        </ToolbarButton>

        <Box sx={{ borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />

        <ToolbarButton
          onClick={openLinkDialog}
          active={editor.isActive('link')}
          title="Insert link"
        >
          <LinkIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().unsetAllMarks().clearNodes().run()
          }
          title="Clear formatting"
        >
          <FormatClearIcon fontSize="small" />
        </ToolbarButton>

        <Box sx={{ borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />

        <ToolbarButton
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertContent({ type: 'verticalSpacer' })
              .run()
          }
          title="Insert vertical spacer"
        >
          <HeightIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal rule"
        >
          <HorizontalRuleIcon fontSize="small" />
        </ToolbarButton>
        <Button
          type="secondary"
          size="small"
          startIcon={<SmartButtonIcon />}
          onClick={openButtonLinkDialog}
          disabled={editor.state.selection.empty}
          sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
        >
          Button
        </Button>
        <Button
          type="secondary"
          size="small"
          startIcon={<LinkIcon />}
          onClick={() => setJotformDialogOpen(true)}
          sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
        >
          Jotform
        </Button>
      </Box>

      <JotformLinkDialog
        open={jotformDialogOpen}
        onClose={() => setJotformDialogOpen(false)}
        onSave={({ href, buttonText }) => {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'jotformLink',
              attrs: { href },
              content: [{ type: 'text', text: buttonText }],
            })
            .run()
          setJotformDialogOpen(false)
        }}
        initial={null}
      />

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="URL"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLinkSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setLinkDialogOpen(false)}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleLinkSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={buttonLinkDialogOpen}
        onClose={() => setButtonLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Button Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="URL"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleButtonLinkSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            type="secondary"
            onClick={() => setButtonLinkDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button type="primary" onClick={handleButtonLinkSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const TemplateEditor = () => {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const isEditing = !!templateId

  const [formData, setFormData] = useState({
    name: '',
    channel: 'email',
    description: '',
    subject: '',
    body: '',
  })
  const [bodyHtml, setBodyHtml] = useState('')
  const [error, setError] = useState(null)
  const [lastFocusedField, setLastFocusedField] = useState('body')
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const [htmlMode, setHtmlMode] = useState(false)
  const subjectRef = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ listItem: false }),
      TiptapListItem.extend({
        content: 'inline*',
        addKeyboardShortcuts() {
          return {
            Enter: ({ editor }) => {
              if (!this.editor.isActive('listItem')) return false
              const { state } = editor
              const { $from, empty } = state.selection
              if (!empty) return false

              for (let d = $from.depth; d > 0; d--) {
                if ($from.node(d).type.name !== 'listItem') continue
                if ($from.node(d).content.size > 0) return false

                const listDepth = d - 1
                const list = $from.node(listDepth)
                const indexInList = $from.index(listDepth)
                const { tr } = state

                if (list.childCount === 1) {
                  const listStart = $from.before(listDepth)
                  const listEnd = $from.after(listDepth)
                  tr.replaceWith(
                    listStart,
                    listEnd,
                    state.schema.nodes.paragraph.create(),
                  )
                  tr.setSelection(
                    state.selection.constructor.near(
                      tr.doc.resolve(listStart + 1),
                    ),
                  )
                } else if (indexInList === list.childCount - 1) {
                  const itemStart = $from.before(d)
                  const itemEnd = $from.after(d)
                  tr.delete(itemStart, itemEnd)
                  const insertPos = tr.mapping.map($from.after(listDepth))
                  tr.insert(insertPos, state.schema.nodes.paragraph.create())
                  tr.setSelection(
                    state.selection.constructor.near(
                      tr.doc.resolve(insertPos + 1),
                    ),
                  )
                } else {
                  const itemStart = $from.before(d)
                  const itemEnd = $from.after(d)
                  tr.delete(itemStart, itemEnd)
                  tr.setSelection(
                    state.selection.constructor.near(tr.doc.resolve(itemStart)),
                  )
                }

                editor.view.dispatch(tr)
                return true
              }
              return false
            },
            Tab: () => this.editor.commands.sinkListItem(this.name),
            'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
          }
        },
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      ButtonLink,
      JotformLink,
      VerticalSpacer,
    ],
    content: '',
    onUpdate: ({ editor: ed }) => {
      const { tr } = ed.state
      let modified = false
      ed.state.doc.descendants((node, pos) => {
        if (
          (node.type.name === 'buttonLink' ||
            node.type.name === 'jotformLink') &&
          node.textContent === ''
        ) {
          tr.delete(pos, pos + node.nodeSize)
          modified = true
        }
      })
      if (modified) {
        ed.view.dispatch(tr)
        return
      }
      const html = ed.getHTML()
      setBodyHtml(html)
      setFormData((prev) => ({ ...prev, body: html }))
    },
    onFocus: () => setLastFocusedField('body'),
  })

  const { data: templatesData } = useTemplateList()
  const templates = useMemo(
    () => templatesData?.contentTemplateList?.templates || [],
    [templatesData],
  )

  const [fetchTemplate, { loading: loadingTemplate }] = useLazyQuery(
    GET_TEMPLATE,
    {
      onCompleted: (data) => {
        if (data?.contentTemplate) {
          const body = data.contentTemplate.bodyTemplate || ''
          setFormData({
            name: data.contentTemplate.name || '',
            channel: data.contentTemplate.channel || 'email',
            description: data.contentTemplate.description || '',
            subject: data.contentTemplate.subjectTemplate || '',
            body,
          })
          setBodyHtml(body)
          if (editor) {
            editor.commands.setContent(body, false)
          }
        }
      },
      onError: () => setError('Failed to load template'),
    },
  )

  const [createTemplate, { loading: creating }] = useMutation(CREATE_TEMPLATE, {
    refetchQueries: ['ListTemplates'],
    onCompleted: () => navigate(ROUTE_METADATA.adminOutreachTemplates.path),
    onError: () => setError('Failed to create template'),
  })

  const [updateTemplate, { loading: updating }] = useMutation(UPDATE_TEMPLATE, {
    refetchQueries: ['ListTemplates'],
    onCompleted: () => navigate(ROUTE_METADATA.adminOutreachTemplates.path),
    onError: () => setError('Failed to update template'),
  })

  const [deleteTemplate, { loading: deleting }] = useMutation(DELETE_TEMPLATE, {
    refetchQueries: ['ListTemplates'],
    onCompleted: () => navigate(ROUTE_METADATA.adminOutreachTemplates.path),
    onError: () => setError('Failed to delete template'),
  })

  useEffect(() => {
    if (isEditing) {
      fetchTemplate({ variables: { id: templateId } })
    }
  }, [isEditing, templateId, fetchTemplate])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleBodyChange = useCallback((html) => {
    setBodyHtml(html)
    setFormData((prev) => ({ ...prev, body: html }))
  }, [])

  const insertVariable = useCallback(
    (varName) => {
      const variable = `{{${varName}}}`

      if (lastFocusedField === 'subject' && subjectRef.current) {
        const input = subjectRef.current
        const start = input.selectionStart ?? input.value.length
        const end = input.selectionEnd ?? input.value.length
        const text = input.value
        const newText =
          text.substring(0, start) + variable + text.substring(end)

        setFormData((prev) => ({ ...prev, subject: newText }))

        setTimeout(() => {
          input.selectionStart = input.selectionEnd = start + variable.length
          input.focus()
        }, 0)
      } else if (editor) {
        editor.chain().focus().insertContent(variable).run()
      }
    },
    [lastFocusedField, editor],
  )

  const handleLoadTemplate = useCallback(
    (templateId) => {
      if (!templateId) return
      const template = templates.find((t) => t.id === templateId)
      if (template) {
        const bodyContent = template.bodyTemplate || ''
        setFormData({
          name: isEditing ? formData.name : `${template.name} (Copy)`,
          channel: template.channel || 'email',
          description: template.description || '',
          subject: template.subjectTemplate || '',
          body: bodyContent,
        })
        setBodyHtml(bodyContent)
        if (editor) {
          editor.commands.setContent(bodyContent, false)
        }
      }
    },
    [templates, isEditing, formData.name, editor],
  )

  const handleSave = useCallback(async () => {
    setError(null)

    if (isEditing) {
      await updateTemplate({
        variables: {
          input: {
            id: templateId,
            name: formData.name,
            description: formData.description || null,
            subjectTemplate: formData.subject || null,
            bodyTemplate: formData.body,
          },
        },
      })
    } else {
      await createTemplate({
        variables: {
          input: {
            name: formData.name,
            channel: formData.channel,
            description: formData.description || null,
            subjectTemplate: formData.subject || null,
            bodyTemplate: formData.body,
          },
        },
      })
    }
  }, [formData, isEditing, templateId, createTemplate, updateTemplate])

  const handleDelete = useCallback(async () => {
    setError(null)
    await deleteTemplate({ variables: { input: { id: templateId } } })
    setDeleteDialogOpen(false)
  }, [templateId, deleteTemplate])

  const handleToggleHtmlMode = useCallback(() => {
    setHtmlMode((prev) => {
      if (prev && editor) {
        editor.commands.setContent(bodyHtml, false)
      }
      return !prev
    })
  }, [editor, bodyHtml])

  if (loadingTemplate) {
    return <Loading />
  }

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          {isEditing ? 'Edit Template' : 'New Template'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTE_METADATA.adminOutreachTemplates.path)}
          >
            Back to Templates
          </Button>
          {isEditing && (
            <>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                sx={{ '& .MuiList-root': { py: 1 } }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null)
                    setCloneDialogOpen(true)
                  }}
                  sx={{ px: 3, py: 1.5, minWidth: 250 }}
                >
                  Clone from existing template
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null)
                    setDeleteDialogOpen(true)
                  }}
                  sx={{ px: 3, py: 1.5, minWidth: 250, color: 'error.main' }}
                >
                  Delete Template
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </AdminPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="h6">Template Details</Typography>
                {!isEditing && (
                  <>
                    <IconButton
                      onClick={(e) => setMenuAnchor(e.currentTarget)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                      sx={{ '& .MuiList-root': { py: 1 } }}
                    >
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null)
                          setCloneDialogOpen(true)
                        }}
                        sx={{ px: 3, py: 1.5, minWidth: 250 }}
                      >
                        Clone from existing template
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  fullWidth
                />
                <Select
                  label="Channel"
                  name="channel"
                  value={formData.channel}
                  onChange={(e) => handleChange('channel', e.target.value)}
                  options={TEMPLATE_CHANNELS.map((ch) => ({
                    name: ch.label,
                    value: ch.value,
                  }))}
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
                {formData.channel === 'email' && (
                  <TextField
                    label="Subject"
                    value={formData.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    onFocus={() => setLastFocusedField('subject')}
                    inputRef={subjectRef}
                    fullWidth
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }} elevation={0}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Body Content
              </Typography>
              {formData.channel === 'highlight_push' ? (
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  value={bodyHtml}
                  onChange={(e) => handleBodyChange(e.target.value)}
                  onFocus={() => setLastFocusedField('body')}
                  placeholder="Enter notification message..."
                />
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: 1,
                    }}
                  >
                    <Button
                      type="secondary"
                      size="small"
                      startIcon={<CodeIcon />}
                      onClick={handleToggleHtmlMode}
                    >
                      {htmlMode ? 'Rich Editor' : 'HTML Source'}
                    </Button>
                  </Box>
                  {htmlMode ? (
                    <TextField
                      multiline
                      rows={14}
                      fullWidth
                      value={bodyHtml}
                      onChange={(e) => handleBodyChange(e.target.value)}
                      onFocus={() => setLastFocusedField('body')}
                      InputProps={{
                        sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .ProseMirror': {
                          minHeight: 300,
                          maxHeight: 400,
                          overflow: 'auto',
                          p: 2,
                          outline: 'none',
                          '& p': { mb: '0.5em' },
                          '& a': { mb: '0.5em' },
                          '& li:last-child': { mb: '0.5em' },
                          '& a[data-button-link]': {
                            display: 'inline-block',
                            padding: '10px 24px',
                            backgroundColor: '#1976d2',
                            color: '#FFFFFF',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                          },
                          '& hr': {
                            border: 'none',
                            borderTop: '2px solid #ccc',
                            width: '80%',
                            mx: 'auto',
                            my: 2,
                          },
                          '& div[data-spacer]': {
                            borderTop: '1px dashed #ccc',
                            position: 'relative',
                          },
                        },
                      }}
                    >
                      <EditorToolbar editor={editor} />
                      <LinkBubble editor={editor} />
                      <ButtonLinkBubble editor={editor} />
                      <JotformLinkBubble editor={editor} />
                      <EditorContent editor={editor} />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {formData.channel !== 'highlight_push' && (
            <Card sx={{ mt: 3 }} elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Available Variables
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Click to insert variable at cursor position (works in subject
                  and body)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {Object.keys(TEMPLATE_VARIABLES).map((varName) => (
                    <Chip
                      key={varName}
                      label={`{{${varName}}}`}
                      onClick={() => insertVariable(varName)}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.main',
                        },
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ height: '100%' }}>
            <CardContent
              sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {formData.channel === 'highlight_push' ? (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    About Highlight Push Notifications
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                    {[
                      "The campaign's tag must have an associated Highlight card. When the notification is delivered, tapping it will open that Highlight card content.",
                      'The notification title will display as "StrivePD".',
                      'Keep the body brief — it appears in the small system notification banner and will be truncated if too long.',
                      'Rich text formatting is not supported. Plain text only.',
                      'Template variables (e.g. patient name) are not currently supported.',
                    ].map((text) => (
                      <Typography
                        key={text}
                        component="li"
                        variant="body1"
                        sx={{ mb: 1 }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                    Preview
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Variables shown with sample data
                  </Typography>
                  <Box sx={{ flex: 1, mt: 3, overflow: 'auto' }}>
                    <TemplatePreviewBox
                      subject={formData.subject}
                      body={formData.body}
                      channel={formData.channel}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 4,
          pt: 3,
          pb: 15,
          mb: 8,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          type="secondary"
          onClick={() => navigate(ROUTE_METADATA.adminOutreachTemplates.path)}
          disabled={creating || updating}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!formData.name || !formData.body || creating || updating}
        >
          {isEditing ? 'Update Template' : 'Create Template'}
        </Button>
      </Box>

      <Dialog
        open={cloneDialogOpen}
        onClose={() => {
          setCloneDialogOpen(false)
          setTemplateSearch('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone from existing template</DialogTitle>
        <DialogContent>
          {templates.length > 0 && (
            <TextField
              placeholder="Search templates..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2, mt: 1 }}
            />
          )}
          <List sx={{ pt: 0 }}>
            {templates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No templates available to clone
              </Typography>
            ) : (
              templates
                .filter((template) => {
                  if (!templateSearch) return true
                  const searchLower = templateSearch.toLowerCase()
                  return (
                    template.name.toLowerCase().includes(searchLower) ||
                    template.channel.toLowerCase().includes(searchLower) ||
                    (template.description || '')
                      .toLowerCase()
                      .includes(searchLower)
                  )
                })
                .map((template) => {
                  const channelLabel =
                    TEMPLATE_CHANNELS.find(
                      (ch) => ch.value === template.channel,
                    )?.label || template.channel
                  return (
                    <ListItem key={template.id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          handleLoadTemplate(template.id)
                          setCloneDialogOpen(false)
                          setTemplateSearch('')
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                              }}
                            >
                              <Typography variant="body1">
                                {template.name}
                              </Typography>
                              <Chip
                                label={channelLabel}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={template.description || null}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this template? This will fail if the
            template is in use by any campaigns.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="secondary"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ backgroundColor: 'error.main' }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TemplateEditor
